import { find } from 'lodash';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  getCollectionSummary,
  getDeliverySummary,
} from 'weborder/selectors/menuSelectors';

import { OrderCheckout as RedesignOrderCheckout } from '../../../pages';
import * as Actions from '../../actions/Actions';
import * as Api from '../../Api';
import * as BasketControl from '../../BasketControl';
import * as Data from '../../Data';
import * as OrderFlow from '../../OrderFlow';
import { basketActions, basketSelectors, userActions } from '../../redux';
import * as Routes from '../../Routes';
import {
  basketSelectors as oldBasketSelectors,
  menuSelectors,
  paymentSelectors,
} from '../../selectors';
import { flagService } from '../../services';
import { storage } from '../../services/storageServices';
import * as Store from '../../Store';
import { default as LegacyCheckout } from './index.weborder';

const pageComponent = flagService.componentToggle(
  'uiRedesign',
  RedesignOrderCheckout,
  LegacyCheckout,
);

const CHECKOUT_LOADING_STATES = [
  'cancelling',
  'loading',
  'submitting',
  'validating',
];

export interface SubmitTypeOptions {
  paypal?: {
    deviceData?: string;
    nonce?: string;
    useSavedAccount?: boolean;
  };
  googleApplePayStripeToken?: string;
}

export type StateProps = ReturnType<typeof mapStateToProps>;
export type DispatchProps = ReturnType<typeof mapDispatchToProps>;

const mapStateToProps = (state: Store.State, ownProps: Routes.Routes) => {
  const deliveryType = ownProps.params.type;
  const isDelivery = deliveryType === 'delivery';
  const vrId = ownProps.params.vrid;
  const prId = !isDelivery ? ownProps.params.id : undefined;
  const deliveryLocationId = isDelivery ? ownProps.params.id : undefined;

  const Restaurant = menuSelectors.getRestaurantDetails(state, {
    deliveryType,
    vrId,
    prId,
    deliveryLocationId,
  });

  const { flipdish } = state;

  let MenuUrl = undefined;
  if (ownProps.params.type === 'collection') {
    const collectionSummary = getCollectionSummary(
      state.flipdish.CollectionSummaries,
      {
        vrId: ownProps.params.vrid,
        prId: ownProps.params.id,
      },
    );
    if (collectionSummary) {
      MenuUrl = collectionSummary.MenuUrl;
    }
  } else {
    const deliverySummary = getDeliverySummary(state.deliveryOptions, {
      vrId: ownProps.params.vrid,
      deliveryLocationId: ownProps.params.id,
    });
    if (deliverySummary) {
      MenuUrl = deliverySummary.MenuUrl;
    }
  }

  let dl: Api.DeliveryLocation | null = null;
  if (isDelivery && state.flipdish.DeliveryLocations.status === 'success') {
    dl =
      find(
        state.flipdish.DeliveryLocations.data,
        (dl) => dl.DeliveryLocationId == ownProps.params.id,
      ) || null;
  }

  const menu = menuSelectors.findMenu(state, {
    vrId,
    deliveryType,
    prId,
    deliveryLocationId,
  });

  const basket = basketSelectors.getBasketBridge(state);
  const basketLoaded = basket && basket.items.length ? true : false;

  const tip =
    typeof state.basket.tip === 'number'
      ? state.basket.tip
      : flipdish.Customer.status === 'success'
      ? flipdish.Customer.data.DefaultTipPercentage
      : 0;

  const orderPayload = basketSelectors.getOrderPayload(state);

  const orderTotalToPay = basketSelectors.getTotalToPay(state);

  const orderTipsTotal = basketSelectors.getTipTotal(state);

  const requestedTimeOrDefault = basketSelectors.getSelectedRequestedTimeOrDefault(
    state,
  );

  const PickupLocationType = basketSelectors.getPickupLocationTypeOrDefault(
    state,
  );

  const Payments = paymentSelectors.getAvailablePayments(state, {
    deliveryType,
    vrId,
    prId,
    deliveryLocationId,
  });
  const Account = basketSelectors.getSelectedPaymentOrDefault(state);
  const UserName =
    flipdish.Customer.status === 'success' &&
    flipdish.Customer.data.CustomerName;

  const hasRequestFullNameFeatureFlag = oldBasketSelectors.getRequestFullNameFeatureFlag(
    state,
  );

  const loading = CHECKOUT_LOADING_STATES.includes(state.basket.status);

  return {
    Account,
    AppConfig: state.flipdish.AppConfig,
    AppDesignScheme: state.appDesignScheme,
    Basket: basket,
    basketLoaded,
    checkoutError: state.basket.error,
    checkoutStatus: state.basket.status,
    checkoutValidationErrors: state.basket.validationErrors,
    ChefNotes: basketSelectors.getChefNotes(state),
    CollectionSummaries: state.flipdish.CollectionSummaries,
    Customer: flipdish.Customer,
    DeliveryLocation: dl,
    DeliverySummaries: state.flipdish.DeliverySummaries,
    deviceId: state.flipdish.Config.DeviceId,
    hasRequestFullNameFeatureFlag,
    isDark: state.flipdish.isDark,
    Languages: state.locale.languages,
    loading,
    location: state.flipdish.Location.geo,
    menu,
    MenuUrl,
    Menus: state.flipdish.MenuData,
    Offset: state.flipdish.Offset,
    Order: state.flipdish.Order,
    orderPayload,
    orderTipsTotal,
    orderTotalToPay,
    Payments,
    PercentageTip: tip,
    PickupLocationType,
    requestedTimeOrDefault,
    Restaurant,
    TableNumber: basketSelectors.getPickupLocationIdOrDefault(state),
    ToastMessages: state.flipdish.ToastMessages,
    translate: getTranslate(state.locale),
    UserName,
    VouchersInProgress: state.flipdish.VouchersInProgress,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<Store.State, {}, Action>,
) => {
  return {
    createBasketSnapshot: (payload: WebOrder.State.BasketSnapshot) => {
      dispatch(basketActions.createBasketSnapshot(payload));
    },
    loginSuccessBridge: () => {
      dispatch(userActions.loginSuccessBridge() as any);
    },
    loginErrorBridge: () => {
      dispatch(userActions.loginErrorBridge() as any);
    },
    startCheckoutFlow: () => {
      dispatch(basketActions.startCheckoutFlow());
    },
    continueCheckoutFlow: () => {
      dispatch(basketActions.continueCheckoutFlow());
    },
    cancelCheckoutFlow: () => {
      dispatch(basketActions.cancelCheckoutFlow());
    },
    setUserName: (payload: { name: string }) => {
      dispatch({
        type: 'SET_CUSTOMER_NAME',
        payload,
      });
    },
    UpdateCustomer: () => {
      dispatch(Actions.UpdateCustomer());
    },

    Submit: (
      order: Api.DeliveryOrder | Api.CollectionOrder,
      vrid: number,
      account: Api.PaymentAccount,
      voucherCode: string,
      chefNote: string,
      basket: BasketControl.Data,
      currency: Api.Currency,
      total: number,
      type?: 'STRIPE' | 'NORMAL' | 'PAYPAL',
      options?: SubmitTypeOptions,
      concessionStoreId?: number,
    ) => {
      dispatch(
        Actions.SubmitOrder(
          order,
          vrid,
          account,
          voucherCode,
          chefNote,
          basket,
          currency,
          total,
          type,
          options,
          concessionStoreId,
        ),
      );
    },
    LoadRestaurant: (routes: Routes.Routes) => {
      dispatch(Actions.LoadRestaurant(routes));
    },

    AddItemToBasket: (payload: BasketControl.Item) => {
      dispatch(Actions.AddToBasket(payload));
    },
    RemoveItemFromBasket: (payload: {
      menuId: number;
      index: number;
      indexName?: string;
    }) => {
      dispatch(Actions.RemoveFromBasket(payload));
    },

    SetTip: (payload: number) => {
      dispatch(Actions.SetTip(payload));
    },

    EditChefNotes: (vrid: number, value: string) => {
      dispatch(Actions.EditChefNotes(vrid, value));
    },

    EditTableNumbers: (vrid: number, value: number) => {
      dispatch(Actions.EditTableNumbers(vrid, value));
    },

    SetRequestedTime: (payload: { menuId: number; time: string }) => {
      dispatch(Actions.SetRequestedTime(payload));
    },

    AddToast: (message: string) => {
      dispatch(Actions.AddToast(message));
    },
    SetOrder: (order: OrderFlow.State) => {
      dispatch(Actions.SetOrder(order));
    },
    SetCustomer: (customer: Data.Async<Api.Customer>) => {
      dispatch({
        type: 'SET_CUSTOMER',
        payload: customer,
      });
      storage.removeItem('flipdish-customer');
    },
    getMenuByUrl: (menuUrl: string) => {
      dispatch(Actions.getMenuByUrl(menuUrl));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(pageComponent);
